import type { ApiMessageEntity } from '../api/types';
import { ApiMessageEntityTypes } from '../api/types';
import type { ForensicEntity, ForensicEntityType } from './types';

const PATTERNS: { type: ForensicEntityType; regex: RegExp }[] = [
  { type: 'EMAIL', regex: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g },
  { type: 'URL', regex: /https?:\/\/[^\s<>"']+/gi },
  { type: 'DOMAIN', regex: /(?:^|[\s(])([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s]*)?/gi },
  { type: 'MSISDN', regex: /(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,4}\)?[-.\s]?)?\d{3,4}[-.\s]?\d{3,4}(?:[-.\s]?\d+)?/g },
  { type: 'NIK', regex: /\b\d{16}\b/g },
  { type: 'IP', regex: /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\b/g },
  { type: 'IMEI', regex: /\b\d{15}\b/g },
  { type: 'IMSI', regex: /\b\d{14,15}\b/g },
  { type: 'PASSPORT', regex: /\b[A-Z]{1,2}\d{6,9}\b/g },
  { type: 'CREDIT_CARD', regex: /\b(?:\d{4}[-\s]?){3}\d{4}\b/g },
  { type: 'BTC_ADDRESS', regex: /\b(?:bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}\b/g },
  { type: 'ETH_ADDRESS', regex: /\b0x[a-fA-F0-9]{40}\b/g },
  { type: 'IBAN', regex: /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g },
  { type: 'USERNAME', regex: /@[a-zA-Z0-9_]{4,32}\b/g },
  { type: 'USERNAME', regex: /(?:t\.me|telegram\.me)\/([a-zA-Z0-9_]{4,32})/gi },
];

function normalizeEntity(type: ForensicEntityType, value: string): string {
  const trimmed = value.trim();
  if (type === 'USERNAME') {
    return trimmed.replace(/^@/, '').toLowerCase();
  }
  if (type === 'EMAIL') {
    return trimmed.toLowerCase();
  }
  return trimmed;
}

function entityId(type: ForensicEntityType, normalized: string): string {
  return `${type}:${normalized}`;
}

function addEntity(
  map: Map<string, ForensicEntity>,
  type: ForensicEntityType,
  raw: string,
) {
  const value = raw.trim();
  if (!value || value.length < 3) return;
  const normalized = normalizeEntity(type, value);
  const id = entityId(type, normalized);
  if (!map.has(id)) {
    map.set(id, { id, type, value, normalized });
  }
}

export function extractEntitiesFromText(text: string): ForensicEntity[] {
  const map = new Map<string, ForensicEntity>();
  if (!text) return [];

  PATTERNS.forEach(({ type, regex }) => {
    const re = new RegExp(regex.source, regex.flags);
    let match = re.exec(text);
    while (match) {
      addEntity(map, type, match[0]);
      match = re.exec(text);
    }
  });

  return Array.from(map.values());
}

export function extractEntitiesFromMessageEntities(
  text: string,
  entities?: ApiMessageEntity[],
): ForensicEntity[] {
  const map = new Map<string, ForensicEntity>();
  extractEntitiesFromText(text).forEach((e) => map.set(e.id, e));

  entities?.forEach((entity) => {
    const slice = text.slice(entity.offset, entity.offset + entity.length);
    if (entity.type === ApiMessageEntityTypes.Mention) {
      addEntity(map, 'USERNAME', slice);
    }
    if (entity.type === ApiMessageEntityTypes.TextUrl) {
      const urlEntity = entity as { url?: string };
      if (urlEntity.url) {
        addEntity(map, 'URL', urlEntity.url);
      }
    }
    if (entity.type === ApiMessageEntityTypes.Url) {
      addEntity(map, 'URL', slice);
    }
    if (entity.type === ApiMessageEntityTypes.Email) {
      addEntity(map, 'EMAIL', slice);
    }
    if (entity.type === ApiMessageEntityTypes.Phone) {
      addEntity(map, 'MSISDN', slice);
    }
    if (entity.type === ApiMessageEntityTypes.Bold || entity.type === ApiMessageEntityTypes.Plain) {
      if (/^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+$/.test(slice.trim())) {
        addEntity(map, 'PERSON', slice.trim());
        addEntity(map, 'NAME', slice.trim());
      }
    }
  });

  return Array.from(map.values());
}
